import { Avatar, Button, Checkbox, Dialog, Pagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import {ProjectConfig} from 'Global';
import { ApiRequest } from "GlobalFunctions";
import ImageIcon from '@mui/icons-material/Image';
import FiltersProducts from 'elements/Filters/FiltersProducts';

export default function PortfoliosEditor(props: any) {
  const { setLoading, openEditor, setOpenEditor, portfolio_id, loadList } = props;
  const { enqueueSnackbar } = useSnackbar();

  const defaultValues = {
    portfolio_id: portfolio_id ? portfolio_id : "null",
    portfolio_name: "",
    products: [] as string[],
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [allProductIds, setAllProductIds] = useState<string[]>([]);
  const [filters, setFilters] = useState([]);

  // Cargar detalles del portfolio si existe
  useEffect(() => {
    if (portfolio_id && openEditor) {
      axios.get(`${ProjectConfig.api_url}/portfolios/details`, {
        params: { portfolio_id },
      })
      .then((response) => {
        const data = response.data.data;
        const products = typeof data.products === 'string'
          ? JSON.parse(data.products)
          : data.products || [];

        setFormValues({
          portfolio_id: data.portfolio_id,
          portfolio_name: data.portfolio_name || "",
          products: products.map((id: any) => String(id))
        });
        setSelectedProducts(products.map((id: any) => String(id)));
      })
      .catch((err) => {
        console.log(err.response);
      });
    } else if (openEditor) {
      setFormValues(defaultValues);
      setSelectedProducts([]);
      setSelectAll(false);
    }
  }, [portfolio_id, openEditor]);

  // Cargar lista de productos
  useEffect(() => {
    if (openEditor) {
      loadProductsList();
    }
  }, [page, openEditor, filters]);

  // Cargar todos los IDs de productos cuando se abre el editor
  useEffect(() => {
    if (openEditor) {
      loadAllProductIds();
    }
  }, [openEditor, filters]);

  const loadProductsList = async () => {
    await ApiRequest({
      url: "/products/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        page: page,
        filters: filters,
      },
      setResponse: (response: any) => {
        setProductsList(response.list);
        setTotalPages(response.total_pages);
      },
    });
  };

  const loadAllProductIds = async () => {
    await ApiRequest({
      url: "/products/list",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        full_list: true,
        filters: filters,
      },
      setResponse: (response: any) => {
        const ids = response.list.map((product: any) => String(product.product_id));
        setAllProductIds(ids);
      },
    });
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectAll(checked);

    if (checked) {
      // Seleccionar todos los productos
      setSelectedProducts([...allProductIds]);
    } else {
      // Deseleccionar todos
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts((prev) => {
      if (prev.includes(productId)) {
        // Deseleccionar
        const newSelected = prev.filter(id => id !== productId);
        // Si deseleccionamos uno, el selectAll debe estar en false
        if (selectAll) {
          setSelectAll(false);
        }
        return newSelected;
      } else {
        // Seleccionar
        const newSelected = [...prev, productId];
        // Verificar si ahora están todos seleccionados
        if (newSelected.length === allProductIds.length) {
          setSelectAll(true);
        }
        return newSelected;
      }
    });
  };

  const handleInputChange = (e: { target: { name: any; value: any }; }) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = async () => {
    if (!formValues.portfolio_name.trim()) {
      enqueueSnackbar('Por favor ingrese un nombre para el portfolio', { variant: 'error' });
      return;
    }

    setLoading(true);

    // Convertir los IDs a números para el backend
    const productsAsNumbers = selectedProducts.map(id => parseInt(id));

    await ApiRequest({
      url: "/portfolios/addedit",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: {
        portfolio_id: formValues.portfolio_id,
        portfolio_name: formValues.portfolio_name,
        products: productsAsNumbers
      },
      setResponse: (response: any) => {
        if(response.status === "success"){
          enqueueSnackbar('Portfolio guardado con éxito', { variant: 'success' });
          setOpenEditor(false);
          loadList();
          // Reset
          setSelectedProducts([]);
          setSelectAll(false);
          setPage(1);
        }else{
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        setLoading(false);
      },
    });
  };

  const handleClose = () => {
    setOpenEditor(false);
    setSelectedProducts([]);
    setSelectAll(false);
    setPage(1);
    setFilters([]);
  };

  return (
    <Dialog
      open={openEditor}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <div className="DialogContainer" style={{ minWidth: '800px', maxHeight: '80vh' }}>
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Portfolio</h2>
              <span className="h2_subtitle">
                {portfolio_id ? 'Editar' : 'Crear'} - {selectedProducts.length} producto(s) seleccionado(s)
              </span>
            </div>
          </div>

          <div className="input_group" style={{ marginBottom: '20px' }}>
            <TextField
              id="portfolio_name"
              name="portfolio_name"
              label="Nombre del Portfolio"
              type="text"
              required
              fullWidth
              value={formValues.portfolio_name}
              onChange={handleInputChange}
            />
          </div>

          <FiltersProducts
            open={true}
            setOpen={() => {}}
            filters={filters}
            setFilters={setFilters}
          />

          <div style={{ marginTop: '20px', maxHeight: '60vh', overflowY: 'auto' }}>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectAll}
                        onChange={handleSelectAll}
                        indeterminate={selectedProducts.length > 0 && selectedProducts.length < allProductIds.length}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell sx={{color:'#a8a8a8'}}>NOMBRE / COD</TableCell>
                    <TableCell sx={{color:'#a8a8a8'}}>PROVEEDOR</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {productsList.map((item, i) => {
                    const isSelected = selectedProducts.includes(String(item.product_id));
                    return (
                      <TableRow
                        key={i}
                        hover
                        role="checkbox"
                        selected={isSelected}
                        onClick={() => handleSelectProduct(String(item.product_id))}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar
                              src={item.image}
                              alt={item.product_name}
                              sx={{ marginRight: 2, width: 32, height: 32 }}
                            >
                              {!item.image && <ImageIcon />}
                            </Avatar>
                            <div>
                              <span className='flex_title'>{item.product_name}</span>
                              <span className='flex_subtitle'>{item.product_code}</span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{item.supplier_name}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
              style={{ marginTop: "20px", display: "flex", justifyContent: "center" }}
            />
          </div>

          <div className="dialogButtons" style={{ marginTop: '20px' }}>
            <Button onClick={handleClose} variant="outlined">Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained">Guardar</Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
